import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API, useAuth } from '../contexts/AuthContext';

const STATUS_LABEL = {
  online: { label: 'Online', cls: 'bg-success' },
  offline: { label: 'Fora do ar', cls: 'bg-danger' },
  unknown: { label: 'Aguardando', cls: 'bg-secondary' },
};

function formatarData(dt) {
  if (!dt) return '—';
  return new Date(dt).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function AdminNotificacoes() {
  const { temPermissao } = useAuth();
  const podeEditar = temPermissao('admin:notificacoes');

  const [tipos, setTipos] = useState([]);
  const [canais, setCanais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');

  // Modal inscritos
  const [modalTipo, setModalTipo] = useState(null);
  const [inscritos, setInscritos] = useState([]);
  const [disponiveis, setDisponiveis] = useState([]);
  const [contatosSel, setContatosSel] = useState(new Set());
  const [buscaContato, setBuscaContato] = useState('');
  const [loadingModal, setLoadingModal] = useState(false);

  // Modal historico
  const [histTipo, setHistTipo] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [loadingHist, setLoadingHist] = useState(false);
  const [verificando, setVerificando] = useState(false);

  // Modal novo sistema
  const NOVO_VAZIO = {
    name: '', description: '', tipo_gatilho: 'tcp',
    target_host: '', target_port: 3389,
    check_interval_min: 5, consecutive_failures_threshold: 2,
    slug: '',
  };
  const [novoForm, setNovoForm] = useState(NOVO_VAZIO);
  const [salvandoNovo, setSalvandoNovo] = useState(false);
  const [erroNovo, setErroNovo] = useState('');


  const carregarTipos = useCallback(async () => {
    try {
      const [rTipos, rCanais] = await Promise.all([
        axios.get(`${API}/notificacoes/tipos`),
        axios.get(`${API}/notificacoes/canais`),
      ]);
      setTipos(rTipos.data.data || []);
      setCanais(rCanais.data.data || []);
    } catch (e) {
      setErro('Erro ao carregar dados.');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshTipos = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notificacoes/tipos`);
      setTipos(data.data || []);
    } catch { /* silencioso — nao interrompe o usuario */ }
  }, []);

  useEffect(() => {
    carregarTipos();
    const id = setInterval(refreshTipos, 60_000);
    return () => clearInterval(id);
  }, [carregarTipos, refreshTipos]);

  async function toggleEnabled(tipo) {
    if (!podeEditar) return;
    try {
      const { data } = await axios.patch(`${API}/notificacoes/tipos/${tipo.id}`, {
        enabled: !tipo.enabled,
      });
      setTipos((prev) => prev.map((t) => (t.id === tipo.id ? data.data : t)));
    } catch {
      Swal.fire('Erro', 'Nao foi possivel atualizar o status.', 'error');
    }
  }

  async function atualizarCanal(tipoId, canalId) {
    if (!podeEditar) return;
    try {
      const { data } = await axios.patch(`${API}/notificacoes/tipos/${tipoId}`, {
        canal_id: canalId ? Number(canalId) : null,
      });
      setTipos((prev) => prev.map((t) => (t.id === tipoId ? data.data : t)));
    } catch {
      Swal.fire('Erro', 'Nao foi possivel atualizar o canal.', 'error');
    }
  }

  async function abrirModalInscritos(tipo) {
    setModalTipo(tipo);
    setContatosSel(new Set());
    setBuscaContato('');
    setLoadingModal(true);
    try {
      const [rIns, rDisp] = await Promise.all([
        axios.get(`${API}/notificacoes/tipos/${tipo.id}/inscritos`),
        axios.get(`${API}/notificacoes/tipos/${tipo.id}/contatos-disponiveis`),
      ]);
      setInscritos(rIns.data.data || []);
      setDisponiveis(rDisp.data.data || []);
    } catch {
      Swal.fire('Erro', 'Nao foi possivel carregar inscritos.', 'error');
    } finally {
      setLoadingModal(false);
    }
    const modal = new window.bootstrap.Modal(document.getElementById('modalInscritos'));
    modal.show();
  }

  function toggleContatoSel(id) {
    setContatosSel((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function adicionarInscritos() {
    if (!contatosSel.size || !modalTipo) return;
    const qtd = contatosSel.size;
    try {
      await Promise.all(
        [...contatosSel].map((id) =>
          axios.post(`${API}/notificacoes/tipos/${modalTipo.id}/inscritos`, { contato_id: id }),
        ),
      );
      const [rIns, rDisp] = await Promise.all([
        axios.get(`${API}/notificacoes/tipos/${modalTipo.id}/inscritos`),
        axios.get(`${API}/notificacoes/tipos/${modalTipo.id}/contatos-disponiveis`),
      ]);
      setInscritos(rIns.data.data || []);
      setDisponiveis(rDisp.data.data || []);
      setContatosSel(new Set());
      setBuscaContato('');
      setTipos((prev) => prev.map((t) => t.id === modalTipo.id
        ? { ...t, total_inscritos: (t.total_inscritos || 0) + qtd } : t));
    } catch {
      Swal.fire('Erro', 'Nao foi possivel adicionar inscritos.', 'error');
    }
  }

  async function removerInscrito(contatoId) {
    if (!modalTipo) return;
    const conf = await Swal.fire({
      title: 'Remover inscrito?',
      text: 'Este contato deixara de receber alertas deste monitor.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Remover',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#b43737',
    });
    if (!conf.isConfirmed) return;
    try {
      await axios.delete(`${API}/notificacoes/tipos/${modalTipo.id}/inscritos/${contatoId}`);
      setInscritos((prev) => prev.filter((c) => c.id !== contatoId));
      setTipos((prev) => prev.map((t) => t.id === modalTipo.id
        ? { ...t, total_inscritos: Math.max(0, (t.total_inscritos || 1) - 1) } : t));
      const rDisp = await axios.get(`${API}/notificacoes/tipos/${modalTipo.id}/contatos-disponiveis`);
      setDisponiveis(rDisp.data.data || []);
    } catch {
      Swal.fire('Erro', 'Nao foi possivel remover inscrito.', 'error');
    }
  }

  async function verificarAgora() {
    setVerificando(true);
    try {
      const { data } = await axios.post(`${API}/notificacoes/verificar`);
      setTipos(data.data || []);
    } catch {
      Swal.fire('Erro', 'Nao foi possivel executar a verificacao.', 'error');
    } finally {
      setVerificando(false);
    }
  }

  async function abrirHistorico(tipo) {
    setHistTipo(tipo);
    setHistorico([]);
    setLoadingHist(true);
    const modal = new window.bootstrap.Modal(document.getElementById('modalHistorico'));
    modal.show();
    try {
      const { data } = await axios.get(`${API}/notificacoes/tipos/${tipo.id}/historico`);
      setHistorico(data.data || []);
    } catch {
      Swal.fire('Erro', 'Nao foi possivel carregar historico.', 'error');
    } finally {
      setLoadingHist(false);
    }
  }

  function abrirNovoSistema() {
    setNovoForm(NOVO_VAZIO);
    setErroNovo('');
    const modal = new window.bootstrap.Modal(document.getElementById('modalNovoSistema'));
    modal.show();
  }

  async function salvarNovoSistema() {
    setSalvandoNovo(true);
    setErroNovo('');
    try {
      const payload = { ...novoForm };
      // Nao envia host/porta pra tipos nao-tcp
      if (payload.tipo_gatilho !== 'tcp') {
        payload.target_host = null;
        payload.target_port = null;
      } else {
        payload.target_port = Number(payload.target_port);
      }
      payload.check_interval_min = Number(payload.check_interval_min);
      payload.consecutive_failures_threshold = Number(payload.consecutive_failures_threshold);
      if (!payload.slug?.trim()) delete payload.slug;
      if (!payload.description?.trim()) delete payload.description;

      await axios.post(`${API}/notificacoes/tipos`, payload);
      await refreshTipos();
      const modal = window.bootstrap.Modal.getInstance(document.getElementById('modalNovoSistema'));
      modal?.hide();
    } catch (e) {
      setErroNovo(e.response?.data?.message || 'Nao foi possivel criar o sistema.');
    } finally {
      setSalvandoNovo(false);
    }
  }

  async function excluirSistema(tipo) {
    const ok = await Swal.fire({
      icon: 'warning',
      title: 'Excluir sistema?',
      html: `<strong>${tipo.name}</strong> sera removido.<br>` +
            '<span style="color:#dc3545;font-size:.85rem">Todos os inscritos e o historico de alertas tambem serao apagados (cascade).</span>',
      showCancelButton: true,
      confirmButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#b43737',
    });
    if (!ok.isConfirmed) return;
    try {
      await axios.delete(`${API}/notificacoes/tipos/${tipo.id}`);
      await refreshTipos();
    } catch (e) {
      Swal.fire('Erro', e.response?.data?.message || 'Nao foi possivel excluir.', 'error');
    }
  }

  const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
  const disponiveisFiltrados = buscaContato.trim()
    ? disponiveis.filter((c) => {
        const q = norm(buscaContato.trim());
        return norm(c.push_name).includes(q)
          || norm(c.numero).includes(q)
          || norm(c.setor).includes(q)
          || norm(c.filial).includes(q);
      })
    : disponiveis;

  if (loading) {
    return (
      <div className="p-4 text-center text-muted">
        <div className="spinner-border spinner-border-sm me-2" />
        Carregando...
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="d-flex align-items-start justify-content-between mb-4 gap-3">
        <div>
          <h4 className="mb-1">Notificacoes Automaticas</h4>
          <p className="text-muted mb-0">Alertas via WhatsApp disparados automaticamente ao detectar indisponibilidade de servicos.</p>
        </div>
        <div className="d-flex gap-2 flex-shrink-0">
          {podeEditar && (
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={abrirNovoSistema}
              title="Cadastrar novo sistema para monitoramento"
            >
              <i className="bi bi-plus-lg me-1" />
              Novo sistema
            </button>
          )}
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={verificarAgora}
            disabled={verificando}
            title="Forca verificacao imediata de todos os monitores"
          >
            <i className={`bi ${verificando ? 'bi-arrow-repeat' : 'bi-arrow-clockwise'} me-1`} />
            {verificando ? 'Verificando...' : 'Verificar agora'}
          </button>
        </div>
      </div>

      {erro && <div className="alert alert-danger">{erro}</div>}

      <div className="row g-3">
        {tipos.map((tipo) => {
          const st = STATUS_LABEL[tipo.last_status] || STATUS_LABEL.unknown;
          return (
            <div key={tipo.id} className="col-12 col-lg-4">
              <div className={`card h-100 ${!tipo.enabled ? 'opacity-75' : ''}`}>
                <div className="card-header d-flex align-items-center justify-content-between gap-2" style={{ paddingTop: '0.35rem', paddingBottom: '0.35rem' }}>
                  <div className="d-flex align-items-center gap-2">
                    <span className={`badge ${st.cls}`}>{st.label}</span>
                    <strong>{tipo.name}</strong>
                  </div>
                  {podeEditar && (
                    <div className="d-flex align-items-center gap-2">
                      <div className="form-check form-switch mb-0">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={!!tipo.enabled}
                          onChange={() => toggleEnabled(tipo)}
                          title={tipo.enabled ? 'Desativar monitor' : 'Ativar monitor'}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger py-0 px-1"
                        onClick={() => excluirSistema(tipo)}
                        title="Excluir sistema"
                      >
                        <i className="bi bi-trash" />
                      </button>
                    </div>
                  )}
                </div>

                <div className="card-body" style={{ padding: '0.5rem 1rem' }}>
                  <p className="text-muted small mb-2">{tipo.description}</p>

                  <div className="mb-2">
                    <label className="form-label small fw-semibold mb-1">Canal de envio</label>
                    <select
                      className="form-select form-select-sm"
                      value={tipo.canal_id || ''}
                      onChange={(e) => atualizarCanal(tipo.id, e.target.value)}
                      disabled={!podeEditar}
                    >
                      <option value="">— Sem canal configurado —</option>
                      {canais.map((c) => (
                        <option key={c.id} value={c.id}>{c.nome || c.instance_name}</option>
                      ))}
                    </select>
                    {!tipo.canal_id && (
                      <div className="form-text text-warning">
                        <i className="bi bi-exclamation-triangle me-1" />
                        Sem canal: alertas serao monitorados mas nao enviados.
                      </div>
                    )}
                  </div>

                  <div className="d-flex flex-column gap-1 small text-muted mb-2">
                    <span>
                      <i className="bi bi-clock me-1" />
                      Ultima verificacao: {formatarData(tipo.last_checked_at)}
                    </span>
                    {tipo.last_alert_sent_at && (
                      <span>
                        <i className="bi bi-bell me-1" />
                        Ultimo alerta: {formatarData(tipo.last_alert_sent_at)}
                      </span>
                    )}
                  </div>

                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => abrirModalInscritos(tipo)}
                    >
                      <i className="bi bi-people me-1" />
                      Inscritos ({tipo.total_inscritos || 0})
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => abrirHistorico(tipo)}
                    >
                      <i className="bi bi-clock-history me-1" />
                      Historico
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal Novo sistema */}
      <div className="modal fade" id="modalNovoSistema" tabIndex="-1">
        <div className="modal-dialog modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-plus-circle me-2" />
                Novo sistema monitorado
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" />
            </div>
            <div className="modal-body">
              {erroNovo && <div className="alert alert-danger py-2">{erroNovo}</div>}

              <div className="mb-3">
                <label className="form-label small fw-semibold mb-1">
                  Nome <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={novoForm.name}
                  onChange={(e) => setNovoForm({ ...novoForm, name: e.target.value })}
                  maxLength={150}
                  placeholder="Ex: Servidor CentraoDB"
                  autoFocus
                />
              </div>

              <div className="mb-3">
                <label className="form-label small fw-semibold mb-1">Descricao</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={novoForm.description}
                  onChange={(e) => setNovoForm({ ...novoForm, description: e.target.value })}
                  maxLength={500}
                  placeholder="Ex: Servidor primario de banco de dados — TCP 1433"
                />
              </div>

              <div className="mb-3">
                <label className="form-label small fw-semibold mb-1">Tipo de gatilho</label>
                <select
                  className="form-select form-select-sm"
                  value={novoForm.tipo_gatilho}
                  onChange={(e) => setNovoForm({ ...novoForm, tipo_gatilho: e.target.value })}
                >
                  <option value="tcp">TCP (checa host:porta)</option>
                  <option value="monitor">Monitor SEFAZ (SOAP)</option>
                </select>
                {novoForm.tipo_gatilho === 'monitor' && (
                  <div className="form-text text-warning small">
                    <i className="bi bi-exclamation-triangle me-1" />
                    Monitor SEFAZ so funciona pra slugs conhecidos pelo cliente SOAP
                    (ex.: sefaz_nfe_mg, sefaz_nfce_mg). Preencha o slug manualmente
                    no campo abaixo.
                  </div>
                )}
              </div>

              {novoForm.tipo_gatilho === 'tcp' && (
                <div className="row g-2 mb-3">
                  <div className="col-8">
                    <label className="form-label small fw-semibold mb-1">
                      Host <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={novoForm.target_host}
                      onChange={(e) => setNovoForm({ ...novoForm, target_host: e.target.value })}
                      maxLength={100}
                      placeholder="192.168.0.99 ou hostname"
                    />
                  </div>
                  <div className="col-4">
                    <label className="form-label small fw-semibold mb-1">
                      Porta <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      value={novoForm.target_port}
                      onChange={(e) => setNovoForm({ ...novoForm, target_port: e.target.value })}
                      min={1}
                      max={65535}
                    />
                  </div>
                </div>
              )}

              <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="form-label small fw-semibold mb-1">Intervalo (min)</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    value={novoForm.check_interval_min}
                    onChange={(e) => setNovoForm({ ...novoForm, check_interval_min: e.target.value })}
                    min={1}
                    max={1440}
                  />
                </div>
                <div className="col-6">
                  <label className="form-label small fw-semibold mb-1">Falhas para alertar</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    value={novoForm.consecutive_failures_threshold}
                    onChange={(e) => setNovoForm({ ...novoForm, consecutive_failures_threshold: e.target.value })}
                    min={1}
                    max={100}
                  />
                </div>
              </div>

              <div className="mb-2">
                <label className="form-label small fw-semibold mb-1">
                  Slug (opcional)
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={novoForm.slug}
                  onChange={(e) => setNovoForm({ ...novoForm, slug: e.target.value })}
                  maxLength={70}
                  placeholder="Deixe em branco para derivar do nome"
                />
                <div className="form-text small">
                  Se ja existir um slug igual, o backend anexa -2, -3, etc.
                  Obrigatorio informar manualmente para tipo_gatilho=monitor.
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary btn-sm" data-bs-dismiss="modal" disabled={salvandoNovo}>
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={salvarNovoSistema}
                disabled={salvandoNovo}
              >
                <i className="bi bi-check2 me-1" />
                {salvandoNovo ? 'Criando...' : 'Criar sistema'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Inscritos */}
      <div className="modal fade" id="modalInscritos" tabIndex="-1">
        <div className="modal-dialog modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-people me-2" />
                {modalTipo?.name} — Inscritos
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" />
            </div>
            <div className="modal-body">
              {loadingModal ? (
                <div className="text-center py-3">
                  <div className="spinner-border spinner-border-sm" />
                </div>
              ) : (
                <>
                  {podeEditar && (
                    <div className="mb-3">
                      <input
                        type="text"
                        className="form-control form-control-sm mb-2"
                        placeholder="Buscar por nome, setor, filial, numero..."
                        value={buscaContato}
                        onChange={(e) => setBuscaContato(e.target.value)}
                      />
                      <div
                        className="list-group list-group-flush border rounded overflow-auto"
                        style={{ maxHeight: '200px' }}
                      >
                        {disponiveisFiltrados.length === 0 ? (
                          <span className="list-group-item text-muted small">
                            {buscaContato.trim() ? 'Nenhum contato encontrado.' : 'Todos os contatos ja estao inscritos.'}
                          </span>
                        ) : (
                          disponiveisFiltrados.map((c) => (
                            <label
                              key={c.id}
                              className="list-group-item list-group-item-action d-flex align-items-center gap-2 py-2"
                              style={{ cursor: 'pointer' }}
                            >
                              <input
                                type="checkbox"
                                className="form-check-input mt-0 flex-shrink-0"
                                checked={contatosSel.has(c.id)}
                                onChange={() => toggleContatoSel(c.id)}
                              />
                              <div className="flex-grow-1 overflow-hidden">
                                <div className="small fw-semibold text-truncate">{c.push_name || c.numero}</div>
                                <div className="text-muted text-truncate" style={{ fontSize: '0.72rem' }}>
                                  {[c.numero, c.setor, c.filial].filter(Boolean).join(' · ')}
                                </div>
                              </div>
                            </label>
                          ))
                        )}
                      </div>
                      {contatosSel.size > 0 && (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm w-100 mt-2"
                          onClick={adicionarInscritos}
                        >
                          <i className="bi bi-plus-lg me-1" />
                          Adicionar selecionados ({contatosSel.size})
                        </button>
                      )}
                    </div>
                  )}

                  {inscritos.length === 0 ? (
                    <p className="text-muted small">Nenhum inscrito ainda.</p>
                  ) : (
                    <ul className="list-group list-group-flush">
                      {inscritos.map((c) => (
                        <li key={c.id} className="list-group-item d-flex justify-content-between align-items-center px-0">
                          <div>
                            <div className="fw-semibold small">{c.push_name || c.numero}</div>
                            <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                              {c.numero}
                              {c.setor ? ` · ${c.setor}` : ''}
                              {c.filial ? ` · ${c.filial}` : ''}
                            </div>
                          </div>
                          {podeEditar && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removerInscrito(c.id)}
                              title="Remover"
                            >
                              <i className="bi bi-x-lg" />
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary btn-sm" data-bs-dismiss="modal">Fechar</button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Historico */}
      <div className="modal fade" id="modalHistorico" tabIndex="-1">
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-clock-history me-2" />
                {histTipo?.name} — Historico de Alertas
              </h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" />
            </div>
            <div className="modal-body">
              {loadingHist ? (
                <div className="text-center py-3">
                  <div className="spinner-border spinner-border-sm" />
                </div>
              ) : historico.length === 0 ? (
                <p className="text-muted small">Nenhum alerta registrado ainda.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm table-hover align-middle">
                    <thead>
                      <tr>
                        <th>Data/Hora</th>
                        <th>Evento</th>
                        <th>Codigo</th>
                        <th>Enviados</th>
                      </tr>
                    </thead>
                    <tbody>
                      {historico.map((h) => (
                        <tr key={h.id}>
                          <td className="text-nowrap small">{formatarData(h.sent_at)}</td>
                          <td>
                            {h.status_to === 'offline'
                              ? <span className="badge bg-danger">Queda</span>
                              : <span className="badge bg-success">Normalizacao</span>}
                          </td>
                          <td className="small text-muted">
                            {h.c_stat ? `${h.c_stat}${h.x_motivo ? ` — ${h.x_motivo}` : ''}` : '—'}
                          </td>
                          <td className="small">{h.recipients_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary btn-sm" data-bs-dismiss="modal">Fechar</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
